import type { IAnyWidgetParams } from '@core/types'

/**
 * CSS colors used in the app (not sure if all).
 */
export const CSS_COLORS = `
  --blk0: #000000;
  --blk0-rgb: 0 0 0;
  --blk1: #121212;
  --blk1-rgb: 18 18 18;
  --blk2: #424242;
  --blk2-rgb: 40 40 40;
  --blk3: #505050;
  --blk3-rgb: 80 80 80;
  --blk4: #3f4145;

  --white: #fff;
  --white-rgb: 255 255 255;
  --red: #fa3b3b;
  --red-rgb: 250 59 59;
  --red2: #f96474;
  --red3: #e90000;
  --red4: #ff4d4f;
  --salmon: #fc8989;
  --orange: #e98c00;
  --orange2: #faad14;
  --green: #2cc056;
  --business-crimson: #f40c70;
  --blue: #0f6bff;

  /* Legacy Gray Colors */
  --gray1: #171717;
  --gray1-rgb: 23 23 23;
  --gray2: #2b2b2b;
  --gray2-rgb: 43 43 43;
  --gray3: #404040;
  --gray3-rgb: 64 64 64;
  --gray4: #9b9b9b;
  --gray4-rgb: 155 155 155;
  --gray5: #bdbdbd;
  --gray5-rgb: 189, 189, 189;
  --gray6: #e2e2e2;
  --gray6-rgb: 226, 226, 226;

  /* Box Shadow (deprecated) */
  --box-shadow: 0 3px 6px rgb(0 0 0 / 16%), 0 3px 6px rgb(0 0 0 / 23%);
  --box-shadow-hover: 0 10px 20px rgb(0 0 0 / 19%), 0 6px 6px rgb(0 0 0 / 23%);
  --box-shadow-subtle: 0 1.5px 3px rgba(0, 0, 0, 0.08), 0 1.5px 3px rgba(0, 0, 0, 0.12);
`

/**
 * CSS variables for spacing, font size, line height, radius, box shadow used in the app
 * @param params - Widget params
 * @returns CSS variables
 */
export const CSS_SCALING_VARIABLES = (params?: IAnyWidgetParams) => `
  /* Spacing */

  --scaling: 1;

  --spacing-1: calc(4px * var(--scaling));
  --spacing-2: calc(8px * var(--scaling));
  --spacing-3: calc(12px * var(--scaling));
  --spacing-4: calc(16px * var(--scaling));
  --spacing-5: calc(24px * var(--scaling));
  --spacing-6: calc(32px * var(--scaling));
  --spacing-7: calc(40px * var(--scaling));
  --spacing-8: calc(48px * var(--scaling));
  --spacing-9: calc(64px * var(--scaling));

  /* Font Size */

  --font-size-1: calc(12px * var(--scaling));
  --font-size-2: calc(14px * var(--scaling));
  --font-size-3: calc(16px * var(--scaling));
  --font-size-4: calc(18px * var(--scaling));
  --font-size-5: calc(20px * var(--scaling));
  --font-size-6: calc(24px * var(--scaling));
  --font-size-7: calc(28px * var(--scaling));
  --font-size-8: calc(35px * var(--scaling));
  --font-size-9: calc(60px * var(--scaling));

  /* Line Height */

  --line-height-1: calc(16px * var(--scaling));
  --line-height-2: calc(20px * var(--scaling));
  --line-height-3: calc(24px * var(--scaling));
  --line-height-4: calc(26px * var(--scaling));
  --line-height-5: calc(28px * var(--scaling));
  --line-height-6: calc(30px * var(--scaling));
  --line-height-7: calc(36px * var(--scaling));
  --line-height-8: calc(40px * var(--scaling));

  /* Radius */

  --radius-factor: ${params && params.ui_border_style === 'hard' ? '0' : '1'};
  --radius-base: calc(
    var(--fw-player-border-radius, var(--spacing-3)) * var(--scaling) *
      var(--radius-factor)
  );
  --radius-1: calc(var(--radius-base) * 0.25);
  --radius-2: calc(var(--radius-base) * 0.33);
  --radius-3: calc(var(--radius-base) * 0.5);
  --radius-4: calc(var(--radius-base) * 0.67);
  --radius-5: calc(var(--radius-base) * 1);
  --radius-6: calc(var(--radius-base) * 1.33);
  --radius-full: calc(var(--radius-factor) * 99999px);

  /* Box Shadow */

  --box-shadow-1: 0 1px rgb(0 0 0 / 0.05);
  --box-shadow-2: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --box-shadow-3: 0 1px 3px 0 rgb(0 0 0 / 0.1),
    0 1px 2px -1px rgb(0 0 0 / 0.1);
  --box-shadow-4: 0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  --box-shadow-5: 0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  --box-shadow-6: 0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
  --box-shadow-7: 0 25px 50px -12px rgb(0 0 0 / 0.25);
`
